import { Link } from 'react-router';
import { SEO } from '@/components/seo/seo';
import { SmartCTA } from '@/components/ui/smart-cta';

/**
 * PPC landing page for the Suffolk Google Ads campaign.
 *
 * Deliberately noindex'd and excluded from prerender + sitemap (see
 * PRERENDER_EXCLUDE in routes.tsx) so it never competes with the organic
 * /councils/suffolk page. Served by a generated extensionless SPA resource.
 *
 * Conversion goal: value-first free eligibility check (SmartCTA -> /check).
 * Keep one goal, evidence-led copy, and no unverifiable stats — ad review
 * crawls this page.
 */

const CHEVRON_TOP = `repeating-linear-gradient(
  -45deg,
  #f97316,
  #f97316 40px,
  #1e40af 40px,
  #1e40af 80px
)`;

const CHEVRON_BOTTOM = `repeating-linear-gradient(
  -45deg,
  #1e40af,
  #1e40af 40px,
  #f97316 40px,
  #f97316 80px
)`;

function PrimaryCTA({ size = 'lg' }: { size?: 'lg' | 'md' }) {
  const sizing = size === 'lg' ? 'px-8 py-4 text-lg' : 'px-6 py-3';
  return (
    <SmartCTA
      guestText="Start Your Free Eligibility Check"
      authText="Go to Dashboard"
      className={`inline-flex items-center justify-center ${sizing} bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25`}
      showArrow
    />
  );
}

function TrustRow({ dark = false }: { dark?: boolean }) {
  const text = dark ? 'text-brand-300' : 'text-surface-600';
  const items = ['Free eligibility check', '£35 flat fee — no commission', 'You keep 100% of any payout'];
  return (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 text-sm ${text}`}>
      {items.map((item) => (
        <span key={item} className="flex items-center gap-2">
          <svg className="w-4 h-4 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {item}
        </span>
      ))}
    </div>
  );
}

export function SuffolkLandingPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Pothole Damage Claim in Suffolk | £35 Claim Pack | Fixtyer"
        description="Hit a pothole in Suffolk? Prepare a professional claim against Suffolk County Council with the DfT RED rating, A-road deterioration data and the missing U-road survey year. £35 flat fee, keep 100%."
        canonical="/lp/suffolk"
        noindex
      />

      {/* ===== Hero ===== */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-8 z-10" style={{ background: CHEVRON_TOP }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-28 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/20 border border-rose-500/30 rounded-full text-rose-300 text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-rose-400 rounded-full animate-pulse" />
                Suffolk: RED-rated overall & on spend (DfT)
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Hit a pothole in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
                  Suffolk?
                </span>
              </h1>

              <p className="mt-6 text-xl text-brand-200 leading-relaxed">
                Don't let the county fob you off. For half of Suffolk's network there's <em>no published U-road
                condition data for 2024/25</em> — and that gap belongs in your claim.
              </p>

              <p className="mt-4 text-brand-300">
                Fixtyer prepares a professional claim pack citing the Highways Act, Suffolk's RED DfT ratings and
                its survey data gap. You submit it yourself and keep every penny of any compensation.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center">
                <PrimaryCTA />
              </div>
              <div className="mt-6">
                <TrustRow dark />
              </div>
            </div>

            {/* Evidence card */}
            <div className="relative lg:pl-8">
              <div className="bg-brand-900/50 rounded-3xl border border-brand-800 p-8">
                <h2 className="font-display text-lg font-semibold text-white mb-6">
                  Why Suffolk claims have real leverage
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-950/60 rounded-xl p-4 border border-brand-800">
                    <div className="text-4xl font-bold text-amber-400">51.7%</div>
                    <div className="text-sm text-brand-300 mt-1">of the network has no U-road condition data for 2024/25</div>
                  </div>
                  <div className="bg-brand-950/60 rounded-xl p-4 border border-brand-800">
                    <div className="text-4xl font-bold text-amber-400">30%</div>
                    <div className="text-sm text-brand-300 mt-1">of A-roads in amber condition (2024) — a five-year high</div>
                  </div>
                  <div className="bg-brand-950/60 rounded-xl p-4 border border-brand-800">
                    <div className="text-4xl font-bold text-amber-400">19,159</div>
                    <div className="text-sm text-brand-300 mt-1">carriageway potholes filled in 2024/25</div>
                  </div>
                  <div className="bg-brand-950/60 rounded-xl p-4 border border-brand-800">
                    <div className="text-4xl font-bold text-rose-400">RED</div>
                    <div className="text-sm text-brand-300 mt-1">DfT overall & spend rating 2025/26</div>
                  </div>
                </div>
                <p className="text-xs text-brand-400 mt-4">
                  Sources: DfT Local Road Maintenance Ratings 2025–26; Suffolk County Council Local Highways
                  Maintenance Transparency Report (June 2025).
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-8 z-10" style={{ background: CHEVRON_BOTTOM }} />
      </section>

      {/* ===== The admission ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-6">
            Suffolk has a gap in its own evidence
          </h2>
          <blockquote className="bg-surface-50 border-l-4 border-accent-500 p-6 rounded-r-xl mb-6">
            <p className="text-surface-800 text-lg italic">
              "During the 2024–25 survey year, SCC transitioned to an alternative survey methodology. As a result,
              survey data for that year is not available."
            </p>
            <cite className="text-sm text-surface-600 mt-2 block not-italic">
              — Suffolk County Council Transparency Report (June 2025)
            </cite>
          </blockquote>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-2xl border border-surface-200 p-6">
              <h3 className="font-semibold text-brand-900 mb-2">Classified roads going backwards</h3>
              <p className="text-surface-700 text-sm">
                After a decade of improvement, A-road green condition fell from 82.1% in 2021 to 67.5% in 2024, with
                amber at 30.0% — the highest in five years. Reactive spending hit 60% in 2023/24, just before the
                deterioration accelerated.
              </p>
            </div>
            <div className="bg-surface-50 rounded-2xl border border-surface-200 p-6">
              <h3 className="font-semibold text-brand-900 mb-2">Why this matters legally</h3>
              <p className="text-surface-700 text-sm">
                To reject your claim, the council must show a "reasonable" inspection system under Section 58 of the
                Highways Act 1980. With no published condition data for 3,467km of U-roads in 2024/25, proving it knew
                the state of your street is much harder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              Three simple steps
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">
              From pothole to professional claim
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                num: '01',
                title: 'Free eligibility check',
                text: 'Answer a few quick questions. Mac confirms whether your claim is worth pursuing and that Suffolk County Council is the responsible authority. Takes about 2 minutes.',
              },
              {
                num: '02',
                title: 'Upload your evidence',
                text: 'Photos of the pothole and damage, plus your repair invoice or quote. Mac reviews each item, flags gaps, and checks FixMyStreet for prior reports of the same defect.',
              },
              {
                num: '03',
                title: 'Get your £35 claim pack',
                text: 'A formal claim letter citing the Highways Act, an organised evidence bundle, and a Suffolk-specific submission guide. You send it — and keep 100% of any payout.',
              },
            ].map((step) => (
              <div key={step.num} className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6">
                <div className="font-mono text-accent-500 text-sm font-semibold mb-3">{step.num}</div>
                <h3 className="font-display text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-brand-300 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <PrimaryCTA size="md" />
          </div>
        </div>
      </section>

      {/* ===== DIY vs pack ===== */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4 text-center">
            Why most DIY claims get rejected
          </h2>
          <p className="text-surface-600 text-center max-w-2xl mx-auto mb-10">
            Councils reject claims that don't engage with their legal defence. A photo and an
            invoice isn't enough — your claim needs to anticipate Section 58.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border-2 border-surface-200">
              <div className="text-rose-600 font-semibold mb-4">A typical DIY claim</div>
              <ul className="space-y-3 text-surface-700 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  Generic template letter with photos attached
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  No road classification or U-road survey-gap data
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  No answer to the council's Section 58 defence
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-rose-400 mt-0.5">✗</span>
                  No check for prior reports of the same pothole
                </li>
              </ul>
              <p className="mt-6 text-sm text-surface-500">Likely outcome: a standard rejection letter.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-accent-500 shadow-xl shadow-accent-500/10">
              <div className="text-accent-600 font-semibold mb-4">Your Fixtyer claim pack — £35</div>
              <ul className="space-y-3 text-surface-700 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-success-500 mt-0.5">✓</span>
                  Formal claim letter citing Section 41 of the Highways Act 1980
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-success-500 mt-0.5">✓</span>
                  Suffolk's U-road survey gap and 2024 deterioration, documented
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-success-500 mt-0.5">✓</span>
                  FixMyStreet prior-report search — a key Section 58 rebuttal
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-success-500 mt-0.5">✓</span>
                  AI-reviewed evidence bundle with gaps flagged before you submit
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-success-500 mt-0.5">✓</span>
                  Suffolk-specific submission guide + follow-up letter templates
                </li>
              </ul>
              <p className="mt-6 text-sm text-accent-600 font-medium">
                One flat fee. No commission. You keep 100% of any compensation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Pricing strip ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-10 text-center">
            Simple, transparent pricing
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-surface-200 p-6 text-center">
              <div className="font-display text-3xl font-bold text-brand-950">Free</div>
              <div className="font-medium text-brand-900 mt-1">Eligibility check</div>
              <p className="text-sm text-surface-600 mt-3">
                Find out if your claim is worth pursuing before spending anything. No card required.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-accent-500 p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-accent-500 text-white text-xs font-semibold rounded-full">
                MOST POPULAR
              </div>
              <div className="font-display text-3xl font-bold text-brand-950">£35</div>
              <div className="font-medium text-brand-900 mt-1">Council claim pack</div>
              <p className="text-sm text-surface-600 mt-3">
                Everything you need to submit a professional claim to Suffolk County Council. One-time fee.
              </p>
            </div>
            <div className="rounded-2xl border border-surface-200 p-6 text-center">
              <div className="font-display text-3xl font-bold text-brand-950">£89 planned</div>
              <div className="font-medium text-brand-900 mt-1">Court pack (not currently available)</div>
              <p className="text-sm text-surface-600 mt-3">
                If the council rejects you, escalate to small claims court with prepared documents.
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-surface-500 mt-6">
            No subscriptions. No percentage fees. No solicitors required.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">Quick answers</h2>
          <div className="space-y-4">
            {[
              {
                q: 'How much can I claim from Suffolk County Council?',
                a: 'Whatever the damage actually cost you — typically tyre, wheel, suspension or tracking repairs. You claim your real repair costs, supported by invoices or quotes. Fixtyer helps you present them properly; you keep 100% of anything recovered.',
              },
              {
                q: 'Does Suffolk performing better than average hurt my claim?',
                a: 'Not automatically. Section 58 requires a reasonable maintenance system for your specific defect, not performance relative to other councils. And Suffolk\'s 2024 data shows classified roads deteriorating, with no published U-road condition data for 2024/25 at all — the gaps that matter for your road.',
              },
              {
                q: 'Is it worth claiming for one tyre?',
                a: 'A single tyre on a modern car is often £100–£250, and pothole strikes frequently damage the wheel or tracking too. The free eligibility check tells you in two minutes whether your claim is worth the £35 pack.',
              },
              {
                q: 'Will the council just reject me anyway?',
                a: 'Councils routinely reject weak claims using the Section 58 "reasonable maintenance" defence. That is exactly why your pack documents Suffolk\'s missing 2024/25 U-road survey year, its 2024 A-road deterioration and prior reports of the defect — the evidence that makes a rejection harder to sustain.',
              },
              {
                q: 'Is Fixtyer a law firm? Do you take a cut?',
                a: 'No and no. Fixtyer is a self-service document-preparation tool, not a solicitor or claims-management company. You pay a flat £35, submit the claim yourself, and keep every penny of any compensation.',
              },
            ].map((item) => (
              <div key={item.q} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{item.q}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-surface-500 mt-6">
            Fixtyer provides document preparation and general guidance, not legal advice. No outcome
            is guaranteed — success depends on the facts and evidence of your individual claim.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="relative py-20 bg-brand-950 text-white overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-8" style={{ background: CHEVRON_TOP }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
            A reversing improvement story demands a precise claim
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Check your eligibility for free. If your claim has legs, a professional pack is £35 —
            and the compensation is all yours.
          </p>
          <PrimaryCTA />
          <p className="text-brand-400 text-sm mt-6">
            Free check · No card required ·{' '}
            <Link to="/councils/suffolk" className="underline hover:text-brand-200">
              Read our full Suffolk road maintenance report
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
